import React, { useState, useEffect, useMemo } from "react";
import {
    Box, Typography, Grid, TextField, Button, MenuItem,
    Stack, Divider, Paper, Avatar, Container, CircularProgress,
    Fade, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    TrendingUp, VerifiedUser, Download,
    Visibility, VisibilityOff, ListAlt, PlayArrowRounded
} from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from "../../contexts/AuthContext";



const inputStyle = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        bgcolor: "#F4F7FE",
        "& fieldset": { borderColor: "transparent" },
        "&:hover fieldset": { borderColor: "#E9EDF7" },
        "&.Mui-focused fieldset": { borderColor: "#4318FF" },
    },
    "& .MuiInputBase-input": { fontWeight: 600, color: "#2B3674" }
};

const FacturesHonoraires = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [, setLoadingIntervenants] = useState(true);
    const [intervenants, setIntervenants] = useState([]);
    const [previewData, setPreviewData] = useState(null);
    const [showDetails, setShowDetails] = useState(true);

    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");
    const [pdfOpen, setPdfOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);

    const resetAll = () => {
        setPreviewData(null);
        setPdfOpen(false);
        setPdfUrl(null);
        setShowDetails(true);

        setFormData({
            startDate: dayjs().startOf('month'),
            endDate: dayjs(),
            intervenantId: '',
            commission: 20
        });
    };
    const rolePath = useMemo(() => {
        const role = Number(user?.role);
        return (role === 1 || role === 2) ? "admin" : "intervenant";
    }, [user]);

    const [formData, setFormData] = useState({
        startDate: dayjs().startOf('month'),
        endDate: dayjs(),
        intervenantId: '',
        commission: 20
    });

    useEffect(() => {
        if (!rolePath || !token) return;
        const loadIntervenants = async () => {
            setLoadingIntervenants(true);
            try {
                const res = await axios.get(`${apiUrl}/${rolePath}/intervenant/l/list_intervenant`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = res.data.result || res.data;
                setIntervenants(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Erreur chargement intervenants:", err.response?.data);
            } finally { setLoadingIntervenants(false); }
        };
        loadIntervenants();
    }, [apiUrl, rolePath, token]);

    // FONCTION DE GÉNÉRATION MANUELLE (DÉCLENCHÉE PAR LE BOUTON)
    const handleGeneratePreview = async () => {
        if (!formData.startDate || !formData.endDate || !formData.intervenantId) return;
        setLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/${rolePath}/genenrate/intervenant/g/invoice/${formData.intervenantId}`, {
                params: {
                    startTime: formData.startDate.format('DD-MM-YYYY'),
                    endTime: formData.endDate.format('DD-MM-YYYY'),
                    preview: 1
                },
                headers: { Authorization: `Bearer ${token}` }
            });
            setPreviewData(response.data.result);
        } catch (error) {
            console.error("Erreur preview:", error);
            setPreviewData(null);
        } finally { setLoading(false); }
    };

    const handleDownload = async () => {
        if (!formData.intervenantId) return;

        setLoading(true);

        try {
            const response = await axios.get(
                `${apiUrl}/${rolePath}/genenrate/intervenant/g/invoice/${formData.intervenantId}`,
                {
                    params: {
                        startTime: formData.startDate.format('DD-MM-YYYY'),
                        endTime: formData.endDate.format('DD-MM-YYYY'),
                        preview: 0
                    },
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    responseType: "blob"
                }
            );

            const file = new Blob([response.data], { type: "application/pdf" });
            const fileURL = URL.createObjectURL(file);

            setPdfUrl(fileURL);
            setPdfOpen(true);

        } catch (error) {
            console.error("Erreur PDF:", error);
        } finally {
            setLoading(false);
        }
    };



    return (
        <Box sx={{ bgcolor: "#F8F9FD", minHeight: "100vh", py: { xs: 4, md: 8 } }}>
            <Container maxWidth="lg">
                {/* --- HEADER --- */}
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 6 }} spacing={2}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: "#2B3674", letterSpacing: "-1px" }}>
                            Note d'honoraires
                        </Typography>
                    </Box>
                    <Chip
                        icon={<VerifiedUser sx={{ color: '#01B574 !important', fontSize: '18px' }} />}
                        label="Système de facturation sécurisé"
                        sx={{ bgcolor: '#E6F6F0', color: '#01B574', fontWeight: 700, borderRadius: '10px', p: 1 }}
                    />
                </Stack>
                <Box sx={{ mb: 4 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: "16px",
                            bgcolor: "#EEF4FF",
                            border: "1px solid #D6E4FF"
                        }}
                    >
                        <Typography sx={{ fontWeight: 600, color: "#2B3674" }}>
                            💡 Sélectionnez un intervenant, choisissez une période, puis générez l’aperçu des honoraires.
                        </Typography>

                        <Typography variant="body2" sx={{ color: "#6B7A99", mt: 0.5 }}>
                            Les montants sont calculés automatiquement à partir des commandes validées sur la période sélectionnée.
                        </Typography>
                    </Paper>
                </Box>
                <Grid container spacing={4}>
                    <Grid item xs={12} lg={7}>
                        <Stack spacing={3}>
                            <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: "1px solid #E9EDF7", boxShadow: "0px 20px 40px rgba(112, 144, 176, 0.05)" }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: "#2B3674", mb: 3 }}>Configuration</Typography>

                                <Stack spacing={3}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#A3AED0", ml: 1, mb: 1, display: 'block' }}>CHOISIR L'INTERVENANT</Typography>
                                        <TextField
                                            select
                                            fullWidth
                                            value={formData.intervenantId}
                                            onChange={(e) => {
                                                const newId = e.target.value;

                                                setFormData(prev => ({
                                                    ...prev,
                                                    intervenantId: newId
                                                }));

                                                // 🔥 reset automatique quand on change d’intervenant
                                                setPreviewData(null);
                                                setPdfOpen(false);
                                                setPdfUrl(null);
                                            }}
                                            sx={inputStyle}
                                            renderValue={(selected) => {
                                                const user = intervenants.find(i => i.id_user === selected);

                                                if (!user) return "Choisir un intervenant";

                                                return (
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Avatar
                                                            src={
                                                                intervenants.photo_user
                                                                    ? `${apiUrl}/storage/workshop/${intervenants.photo_user}`
                                                                    : ""
                                                            }
                                                            sx={{ width: 28, height: 28 }}
                                                        >
                                                            {intervenants.nom_user?.charAt(0)}{intervenants.prenom_user?.charAt(0)}
                                                        </Avatar>

                                                        <Typography sx={{ fontWeight: 700 }}>
                                                            {user.nom_user} {user.prenom_user}
                                                        </Typography>
                                                    </Stack>
                                                );
                                            }}
                                        >                                        {intervenants.map((i) => (
                                            <MenuItem key={i.id_user} value={i.id_user}>
                                                <Stack direction="row" spacing={2} alignItems="center">

                                                    <Avatar
                                                        src={
                                                            i.photo_user
                                                                ? `${apiUrl}/storage/workshop/${i.photo_user}`
                                                                : ""
                                                        }
                                                        sx={{ width: 32, height: 32 }}
                                                    >
                                                        {i.nom_user?.charAt(0)}{i.prenom_user?.charAt(0)}
                                                    </Avatar>

                                                    <Box>
                                                        <Typography sx={{ fontWeight: 700, color: "#2B3674" }}>
                                                            {i.nom_user} {i.prenom_user}
                                                        </Typography>

                                                        <Typography variant="caption" sx={{ color: "#A3AED0" }}>
                                                            {i.email_user}
                                                        </Typography>
                                                    </Box>

                                                </Stack>
                                            </MenuItem>
                                        ))}
                                        </TextField>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#A3AED0", ml: 1, mb: 1, display: 'block' }}>DATE DE DÉBUT</Typography>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker value={formData.startDate} onChange={(v) => setFormData(prev => ({ ...prev, startDate: v }))} slotProps={{ textField: { fullWidth: true, sx: inputStyle } }} />
                                            </LocalizationProvider>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#A3AED0", ml: 1, mb: 1, display: 'block' }}>DATE DE FIN</Typography>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker value={formData.endDate} onChange={(v) => setFormData(prev => ({ ...prev, endDate: v }))} slotProps={{ textField: { fullWidth: true, sx: inputStyle } }} />
                                            </LocalizationProvider>
                                        </Grid>
                                    </Grid>

                                    {/* BOUTON GÉNÉRER APERÇU */}
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleGeneratePreview}
                                        disabled={loading || !formData.intervenantId}
                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowRounded />}
                                        sx={{
                                            bgcolor: "#4318FF",
                                            py: 1.8,
                                            borderRadius: "14px",
                                            fontWeight: 700,
                                            textTransform: "none",
                                            fontSize: "1rem",
                                            boxShadow: "0px 10px 20px rgba(67, 24, 255, 0.2)",
                                            "&:hover": { bgcolor: "#3311DB" }
                                        }}
                                    >
                                        {loading ? "Chargement..." : "Générer l'aperçu"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={resetAll}
                                        disabled={loading}
                                        sx={{
                                            mt: 2,
                                            borderRadius: "14px",
                                            fontWeight: 700,
                                            textTransform: "none",
                                            borderColor: "#E9EDF7",
                                            color: "#2B3674",
                                            "&:hover": {
                                                borderColor: "#4318FF",
                                                color: "#4318FF"
                                            }
                                        }}
                                    >
                                        Réinitialiser
                                    </Button>
                                </Stack>
                            </Paper>

                            {/* --- TABLEAU DÉTAILLÉ --- */}
                            <Fade in={!!previewData}>
                                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "24px", border: "1px solid #E9EDF7", overflow: 'hidden' }}>
                                    <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: "#FFF" }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <ListAlt sx={{ color: "#4318FF" }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#2B3674" }}>Prestations détaillées</Typography>
                                        </Stack>
                                        <Button
                                            size="small"
                                            onClick={() => setShowDetails(!showDetails)}
                                            startIcon={showDetails ? <VisibilityOff /> : <Visibility />}
                                            sx={{ textTransform: "none", fontWeight: 700 }}
                                        >
                                            {showDetails ? "Masquer" : "Afficher"}
                                        </Button>
                                    </Box>
                                    {showDetails && (
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: "#F4F7FE" }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, color: "#A3AED0" }}>Prestation / Réf</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 700, color: "#A3AED0" }}>
                                                        Date
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 700, color: "#A3AED0" }}>Quantité</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, color: "#A3AED0" }}>Total HT</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody sx={{ bgcolor: "#FFF" }}>
                                                {previewData?.items?.map((item, index) => (
                                                    <TableRow key={index} hover>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#2B3674" }}>{item.item}</Typography>
                                                            <Typography variant="caption" sx={{ color: "#4318FF", fontWeight: 600 }}>{item.refCommands}</Typography>
                                                        </TableCell>
                                                        {/* DATE */}
                                                        <TableCell align="center">
                                                            <Typography sx={{ fontWeight: 600, color: "#2B3674" }}>
                                                                {item.date}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 600 }}>{item.quantity}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, color: "#2B3674" }}>{item.totalHT.toFixed(2)} €</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </TableContainer>
                            </Fade>
                        </Stack>
                    </Grid>

                    {/* --- RÉSUMÉ (COLONNE DROITE) --- */}
                    <Grid item xs={12} lg={5}>
                        <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", background: "linear-gradient(135deg, #111C44 0%, #0B1437 100%)", color: "#FFF", position: 'sticky', top: 24 }}>
                            <Stack spacing={4}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Résumé Financier</Typography>

                                <Box sx={{ p: 3, borderRadius: '20px', bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <Typography variant="caption" sx={{ color: "#A3AED0", letterSpacing: 1 }}>CHIFFRE D'AFFAIRES BRUT</Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 800, mt: 1 }}>
                                        {previewData ? `${Number(previewData.totals?.brut || 0).toLocaleString()} €` : "0.00 €"}
                                    </Typography>
                                </Box>

                                <Stack spacing={2}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography sx={{ color: "#A3AED0" }}>Commission ({previewData?.shipping?.commission_rate || formData.commission}%)</Typography>
                                        <Typography sx={{ fontWeight: 700, color: "#FF5B5B" }}>
                                            {previewData ? `-${Number(previewData.totals?.commission_amt || 0).toLocaleString()} €` : "---"}
                                        </Typography>
                                    </Stack>
                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: "#01B574", fontWeight: 800 }}>NET À PAYER</Typography>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                                            <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: -1 }}>
                                                {previewData ? `${Number(previewData.totals?.net_payable || 0).toLocaleString()} €` : "--- €"}
                                            </Typography>
                                            <Avatar sx={{ bgcolor: 'rgba(1, 181, 116, 0.2)', color: '#01B574' }}><TrendingUp /></Avatar>
                                        </Stack>
                                    </Box>
                                </Stack>

                                <Button
                                    variant="contained" fullWidth onClick={handleDownload}
                                    disabled={!previewData || loading} startIcon={<Download />}
                                    sx={{
                                        bgcolor: "#4318FF",
                                        py: 2,
                                        borderRadius: "16px",
                                        fontWeight: 800,
                                        "&:hover": { bgcolor: "#3311DB" }
                                    }}
                                >
                                    Générer le PDF Officiel
                                </Button>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
                <Dialog
                    open={pdfOpen}
                    onClose={() => {
                        setPdfOpen(false);
                        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
                        setPdfUrl(null);
                    }}
                    fullWidth
                    maxWidth="lg"
                >
                    <Box sx={{ height: "85vh", display: "flex", flexDirection: "column" }}>

                        {/* HEADER */}
                        <Box sx={{
                            p: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderBottom: "1px solid #eee"
                        }}>
                            <Typography sx={{ fontWeight: 700 }}>
                                Aperçu PDF
                            </Typography>

                            <Button
                                onClick={() => {
                                    setPdfOpen(false);
                                    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
                                    setPdfUrl(null);
                                }}
                            >
                                Fermer
                            </Button>
                        </Box>

                        {/* PDF VIEWER */}
                        <Box sx={{ flex: 1 }}>
                            {pdfUrl && (
                                <iframe
                                    src={pdfUrl}
                                    width="100%"
                                    height="100%"
                                    style={{ border: "none" }}
                                />
                            )}
                        </Box>

                    </Box>
                </Dialog>
            </Container>
        </Box>
    );
};

export default FacturesHonoraires;